import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/feature/Layout';
import { fetchUsageRecipes, incrementUsageRecipeHelpful, incrementUsageRecipeHelpfulWithUser } from '../../repositories/recipes';
import type { UsageRecipe } from '../../types/recipes';
import { useSEO, generateWebPageSchema } from '../../utils/seo';
import { useAuth } from '../../hooks/useAuth';

const HELPFUL_STORAGE_KEY = 'kyokore_helpful_recipes';

function loadHelpfulIds(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(HELPFUL_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHelpfulIds(ids: string[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(HELPFUL_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // 失敗しても致命的ではないので握りつぶしでOK
  }
}

export default function RecipesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recipes, setRecipes] = useState<UsageRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState<string>('all');
  const [selectedScene, setSelectedScene] = useState<string>('all');
  const [sortKey, setSortKey] = useState<'newest' | 'helpful'>('newest');
  const [keyword, setKeyword] = useState('');
  const [gradeFilter, setGradeFilter] = useState<'all' | number>('all');
  const [helpfulClicked, setHelpfulClicked] = useState<Set<string>>(new Set());
  const [showLoginModal, setShowLoginModal] = useState(false);

  // SEO設定
  useSEO({
    title: '活用レシピ｜キョウコレ',
    description: '教材や教材サイトの具体的な活用方法を共有しています。教科・シーン・学年別に検索して、授業や居場所での実践アイデアを見つけましょう。',
    keywords: '活用レシピ,教材活用,授業アイデア,学習支援,居場所,実践例,教材使い方',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/recipes',
    jsonLd: generateWebPageSchema(
      '活用レシピ｜キョウコレ',
      '教材や教材サイトの具体的な活用方法を共有しています。教科・シーン・学年別に検索して、授業や居場所での実践アイデアを見つけましょう。',
      '/recipes'
    ),
  });

  useEffect(() => {
    loadRecipes();
    // localStorage から既にクリック済みのIDを読み込む
    const ids = loadHelpfulIds();
    setHelpfulClicked(new Set(ids));
  }, []);

  const loadRecipes = async () => {
    try {
      setLoading(true);
      const data = await fetchUsageRecipes();
      setRecipes(data);
    } catch (error) {
      console.error('活用レシピの取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  // すべての検索条件をデフォルトに戻す
  const handleResetFilters = () => {
    setSelectedSubject('all');
    setSelectedScene('all');
    setKeyword('');
    setGradeFilter('all');
    setSortKey('newest');
  };

  // 学年を表示用ラベルに変換
  const getGradeLabel = (grade: number): string => {
    if (grade <= 6) return `小${grade}`;
    if (grade <= 9) return `中${grade - 6}`;
    return `高${grade - 9}`;
  };

  // いずれかのフィルタが有効かどうか
  const hasAnyActiveFilter =
    selectedSubject !== 'all' ||
    selectedScene !== 'all' ||
    keyword.trim() !== '' ||
    gradeFilter !== 'all' ||
    sortKey !== 'newest';

  // 教科の一覧を抽出
  const subjects = Array.from(new Set(recipes.map((r) => r.subject).filter(Boolean))) as string[];

  // フィルタリング
  const filtered = recipes.filter((recipe) => {
    if (selectedSubject !== 'all' && recipe.subject !== selectedSubject) {
      return false;
    }
    if (selectedScene !== 'all' && recipe.scene !== selectedScene) {
      return false;
    }

    // キーワードフィルタ
    const q = keyword.trim().toLowerCase();
    if (q) {
      const title = (recipe.title || '').toLowerCase();
      const summary = (recipe.summary || '').toLowerCase();
      if (!title.includes(q) && !summary.includes(q)) {
        return false;
      }
    }

    // 学年フィルタ
    if (gradeFilter !== 'all') {
      const g = gradeFilter;
      const min = recipe.grade_min ?? 1;
      const max = recipe.grade_max ?? 12;
      if (g < min || g > max) {
        return false;
      }
    }

    return true;
  });

  // 並び替え
  const sorted = [...filtered].sort((a, b) => {
    if (sortKey === 'helpful') {
      const ah = a.helpful_count ?? 0;
      const bh = b.helpful_count ?? 0;
      if (ah !== bh) {
        // 「役に立った」が多い順（降順）
        return bh - ah;
      }
      // 同点の場合は新着順
    }

    // デフォルト：新着順（created_at の降順）
    const aTime = new Date(a.created_at).getTime();
    const bTime = new Date(b.created_at).getTime();
    return bTime - aTime;
  });

  // 対象情報を組み立て
  const buildTargetInfo = (recipe: UsageRecipe): string => {
    const parts: string[] = [];
    
    if (recipe.grade_min !== null && recipe.grade_max !== null) {
      if (recipe.grade_min === recipe.grade_max) {
        if (recipe.grade_min <= 6) {
          parts.push(`小${recipe.grade_min}`);
        } else if (recipe.grade_min <= 9) {
          parts.push(`中${recipe.grade_min - 6}`);
        } else {
          parts.push(`高${recipe.grade_min - 9}`);
        }
      } else {
        const minLabel = recipe.grade_min <= 6 ? `小${recipe.grade_min}` : recipe.grade_min <= 9 ? `中${recipe.grade_min - 6}` : `高${recipe.grade_min - 9}`;
        const maxLabel = recipe.grade_max <= 6 ? `小${recipe.grade_max}` : recipe.grade_max <= 9 ? `中${recipe.grade_max - 6}` : `高${recipe.grade_max - 9}`;
        parts.push(`${minLabel}〜${maxLabel}`);
      }
    }
    
    if (recipe.scene) {
      parts.push(recipe.scene);
    }
    
    if (recipe.time_minutes) {
      parts.push(`${recipe.time_minutes}分`);
    }
    
    if (recipe.group_type) {
      parts.push(recipe.group_type);
    }
    
    return parts.length > 0 ? parts.join(' / ') : '対象情報なし';
  };

  const handleHelpfulClick = async (recipeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (helpfulClicked.has(recipeId)) return;

    if (!user) {
      setShowLoginModal(true);
      return;
    }

    try {
      const updated = await incrementUsageRecipeHelpfulWithUser(recipeId);
      
      if (updated?.helpful_count != null) {
        setRecipes((prev) =>
          prev.map((r) =>
            r.id === recipeId ? { ...r, helpful_count: updated.helpful_count } : r
          )
        );
      } else {
        setRecipes((prev) =>
          prev.map((r) =>
            r.id === recipeId ? { ...r, helpful_count: (r.helpful_count || 0) + 1 } : r
          )
        );
      }

      setHelpfulClicked((prev) => new Set([...prev, recipeId]));
      
      const ids = loadHelpfulIds();
      if (!ids.includes(recipeId)) {
        ids.push(recipeId);
        saveHelpfulIds(ids);
      }
    } catch (error) {
      console.error('役に立ったボタンのエラー:', error);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">活用レシピ</h1>
            <p className="text-gray-600">
              教材や教材サイトの具体的な活用方法を共有しています
            </p>
          </div>

          {/* フィルターエリア */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
            {/* 1段目：教科・シーン */}
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  教科
                </label>
                <select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                >
                  <option value="all">すべて</option>
                  {subjects.map((subject) => (
                    <option key={subject} value={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  シーン
                </label>
                <select
                  value={selectedScene}
                  onChange={(e) => setSelectedScene(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                >
                  <option value="all">すべて</option>
                  <option value="居場所">居場所</option>
                  <option value="家庭">家庭</option>
                  <option value="自主学習">自主学習</option>
                  <option value="オンライン">オンライン</option>
                </select>
              </div>
            </div>

            {/* 2段目：キーワード・対象学年・並び替え */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* キーワード */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  キーワード
                </label>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="タイトルや説明文で検索"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* 対象学年 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  対象学年
                </label>
                <select
                  value={gradeFilter === 'all' ? 'all' : String(gradeFilter)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setGradeFilter(v === 'all' ? 'all' : Number(v));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                >
                  <option value="all">すべて</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((g) => (
                    <option key={g} value={g}>
                      {g <= 6 ? `小${g}` : g <= 9 ? `中${g - 6}` : `高${g - 9}`}
                    </option>
                  ))}
                </select>
              </div>

              {/* 並び替え */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  並び替え
                </label>
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as 'newest' | 'helpful')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                >
                  <option value="newest">新着順（デフォルト）</option>
                  <option value="helpful">「役に立った」が多い順</option>
                </select>
              </div>
            </div>

            {/* 条件をすべてクリアボタン */}
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={handleResetFilters}
                className="px-4 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                条件をすべてクリア
              </button>
            </div>
          </div>

          {/* 検索条件サマリー */}
          {hasAnyActiveFilter && (
            <div className="mb-6">
              <div className="flex flex-wrap gap-2">
                {/* 教科バッジ */}
                {selectedSubject !== 'all' && (
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] text-slate-700 border border-slate-200">
                    <span className="mr-1">教科: {selectedSubject}</span>
                    <button
                      type="button"
                      onClick={() => setSelectedSubject('all')}
                      className="ml-1 text-slate-400 hover:text-slate-600 cursor-pointer"
                      aria-label="教科フィルタをクリア"
                    >
                      ×
                    </button>
                  </span>
                )}

                {/* シーンバッジ */}
                {selectedScene !== 'all' && (
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] text-slate-700 border border-slate-200">
                    <span className="mr-1">シーン: {selectedScene}</span>
                    <button
                      type="button"
                      onClick={() => setSelectedScene('all')}
                      className="ml-1 text-slate-400 hover:text-slate-600 cursor-pointer"
                      aria-label="シーンフィルタをクリア"
                    >
                      ×
                    </button>
                  </span>
                )}

                {/* キーワードバッジ */}
                {keyword.trim() !== '' && (
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] text-slate-700 border border-slate-200">
                    <span className="mr-1">
                      キーワード: {keyword.length > 20 ? `${keyword.slice(0, 20)}...` : keyword}
                    </span>
                    <button
                      type="button"
                      onClick={() => setKeyword('')}
                      className="ml-1 text-slate-400 hover:text-slate-600 cursor-pointer"
                      aria-label="キーワードをクリア"
                    >
                      ×
                    </button>
                  </span>
                )}

                {/* 学年バッジ */}
                {gradeFilter !== 'all' && (
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] text-slate-700 border border-slate-200">
                    <span className="mr-1">対象: {getGradeLabel(gradeFilter)}</span>
                    <button
                      type="button"
                      onClick={() => setGradeFilter('all')}
                      className="ml-1 text-slate-400 hover:text-slate-600 cursor-pointer"
                      aria-label="学年フィルタをクリア"
                    >
                      ×
                    </button>
                  </span>
                )}

                {/* 並び替えバッジ */}
                {sortKey !== 'newest' && (
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] text-slate-700 border border-slate-200">
                    <span className="mr-1">並び: 役に立った順</span>
                    <button
                      type="button"
                      onClick={() => setSortKey('newest')}
                      className="ml-1 text-slate-400 hover:text-slate-600 cursor-pointer"
                      aria-label="並び替えをリセット"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* 投稿ボタン */}
          <div className="mb-6 flex justify-end">
            <button
              onClick={() => navigate('/recipes/new')}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              レシピを投稿する
            </button>
          </div>

          {/* 一覧 */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              <p className="mt-4 text-gray-600">読み込み中…</p>
            </div>
          ) : sorted.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8 text-center">
              <i className="ri-file-list-3-line text-6xl text-gray-400 mb-4"></i>
              <p className="text-gray-600">公開されている活用レシピはまだありません</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {sorted.map((recipe) => {
                const isHelpfulClicked = helpfulClicked.has(recipe.id);
                
                return (
                  <div
                    key={recipe.id}
                    className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow border border-slate-100 overflow-hidden"
                  >
                    {/* サムネイル枠：常にノート風 */}
                    <div className="aspect-video w-full overflow-hidden">
                      <div className="recipe-note-thumbnail flex items-center">
                        <p className="recipe-note-title">
                          {recipe.title}
                        </p>
                      </div>
                    </div>
                  
                    <div className="p-6">
                      <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2">
                        {recipe.title}
                      </h3>
                    
                      <div className="text-xs text-gray-600 mb-3">
                        {buildTargetInfo(recipe)}
                      </div>
                    
                      {/* 紐づきタグ */}
                      {(recipe.material_id || recipe.site_id) && (
                        <div className="mb-3 flex flex-wrap gap-1.5">
                          {recipe.material_id && (
                            <span className="inline-flex items-center rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-700 border border-teal-100">
                              <span className="mr-1 inline-block text-[10px]">📘</span>
                              単元ガイドあり
                            </span>
                          )}
                          {recipe.site_id && (
                            <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 border border-indigo-100">
                              <span className="mr-1 inline-block text-[10px]">🧩</span>
                              教材サイトあり
                            </span>
                          )}
                        </div>
                      )}
                    
                      {recipe.summary && (
                        <p className="text-sm text-gray-700 mb-4 line-clamp-3">
                          {recipe.summary.length > 80
                            ? `${recipe.summary.slice(0, 80)}...`
                            : recipe.summary}
                        </p>
                      )}
                    
                      {/* 役に立ったボタン */}
                      <div className="flex items-center gap-2 mb-3">
                        <button
                          onClick={(e) => handleHelpfulClick(recipe.id, e)}
                          disabled={isHelpfulClicked}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            isHelpfulClicked
                              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                              : 'bg-teal-50 text-teal-700 hover:bg-teal-100'
                          }`}
                        >
                          <i className="ri-thumb-up-line"></i>
                          <span>
                            {isHelpfulClicked ? '役に立った済' : '役に立った'}
                          </span>
                        </button>
                        {recipe.helpful_count > 0 && (
                          <span className="text-xs text-gray-500">
                            {recipe.helpful_count}件
                          </span>
                        )}
                      </div>
                    
                      <button
                        onClick={() => navigate(`/recipes/${recipe.id}`)}
                        className="w-full px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        レシピを見る
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 未ログイン時のモーダル */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              「役に立った」を記録するにはログインが必要です
            </h3>
            <p className="text-gray-700 mb-6">
              ログインすると、あとから「役に立ったレシピ一覧」からまとめて見返せます。
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowLoginModal(false);
                  navigate('/login');
                }}
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-center cursor-pointer whitespace-nowrap"
              >
                ログインして続ける
              </button>
              <button
                onClick={() => setShowLoginModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                今回は閉じる
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
